import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Infinity as InfinityIcon, Microscope, Beaker, Zap, ShoppingBag, Plus, X, Save, Upload } from 'lucide-react';
import { formatPrice } from '../lib/currency';
import InquiryForm from '../components/InquiryForm';
import ProductCard, { Product, Category } from '../components/ProductCard';
import ParticleBackground from '../components/ParticleBackground';
import { useSupabaseProducts } from '../hooks/useSupabaseProducts';
import { useSupabaseNews } from '../hooks/useSupabaseNews';
import { useSiteSettings } from '../hooks/useSiteSettings';
import ProductImageGallery from '../components/ProductImageGallery';
import ProductEditForm from '../components/ProductEditForm';
import { compressProductImage } from '../lib/imageCompression';

export default function UnlimitedPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = (i18n.language === 'cz' ? 'cs' : i18n.language) as 'cs' | 'en' | 'de' | 'sk';
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showNewsManager, setShowNewsManager] = useState(false);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [editTexts, setEditTexts] = useState({
    researchTitle: '',
    researchDesc: '',
    prototypesTitle: '',
    prototypesDesc: '',
    experimentalTitle: '',
    experimentalDesc: ''
  });

  const isAdmin = new URLSearchParams(location.search).get('admin') === 'true' ||
                  localStorage.getItem('admin_logged_in') === 'true';
  const { settings, updateSetting } = useSiteSettings();

  const {
    products,
    categories,
    loading,
    error,
    addProduct,
    editProduct: updateProduct,
    removeProduct,
    addCategory,
    editCategory,
    removeCategory
  } = useSupabaseProducts('unlimited');

  // Save current section to sessionStorage for navigation
  useEffect(() => {
    sessionStorage.setItem('currentSection', 'unlimited');
  }, []);

  // Cleanup scroll on unmount or when all modals are closed
  useEffect(() => {
    return () => {
      // Always restore scroll when component unmounts
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Reset scroll if all modals are closed
  useEffect(() => {
    if (!showProductDetails && !showEditModal && !showCategoryManager && !showNewsManager && !showTextEditor) {
      document.body.style.overflow = 'unset';
    }
  }, [showProductDetails, showEditModal, showCategoryManager, showNewsManager, showTextEditor]);

  // Load texts from settings
  useEffect(() => {
    if (settings) {
      setEditTexts({
        researchTitle: settings['unlimited_research_title_cz'] || t('unlimited.researchTitle'),
        researchDesc: settings['unlimited_research_desc_cz'] || t('unlimited.researchDesc'),
        prototypesTitle: settings['unlimited_prototypes_title_cz'] || t('unlimited.prototypesTitle'),
        prototypesDesc: settings['unlimited_prototypes_desc_cz'] || t('unlimited.prototypesDesc'),
        experimentalTitle: settings['unlimited_experimental_title_cz'] || t('unlimited.experimentalTitle'),
        experimentalDesc: settings['unlimited_experimental_desc_cz'] || t('unlimited.experimentalDesc')
      });
    }
  }, [settings, t]);

  const handleSaveTexts = async () => {
    try {
      await updateSetting('unlimited_research_title_cz', editTexts.researchTitle);
      await updateSetting('unlimited_research_desc_cz', editTexts.researchDesc);
      await updateSetting('unlimited_prototypes_title_cz', editTexts.prototypesTitle);
      await updateSetting('unlimited_prototypes_desc_cz', editTexts.prototypesDesc);
      await updateSetting('unlimited_experimental_title_cz', editTexts.experimentalTitle);
      await updateSetting('unlimited_experimental_desc_cz', editTexts.experimentalDesc);
      setShowTextEditor(false);
      alert('Texty byly úspěšně uloženy!');
    } catch (error) {
      console.error('Error saving texts:', error);
      alert('Chyba při ukládání textů');
    }
  };

  const handleEditProduct = async (updatedProduct: Product) => {
    await updateProduct(updatedProduct);
    setShowEditModal(false);
    setEditingProduct(null);
  };

  const handleDeleteProduct = (id: string) => {
    if (confirm('Opravdu chcete smazat tento produkt?')) {
      removeProduct(id).catch(err => {
        console.error('Error deleting product:', err);
      });
    }
  };

  const handleSaveCategories = (updatedCategories: Category[]) => {
    // This will be handled by the CategoryManager component
    console.log('Categories updated:', updatedCategories);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newProducts = [...products];
    const draggedProduct = newProducts[draggedIndex];
    newProducts.splice(draggedIndex, 1);
    newProducts.splice(dropIndex, 0, draggedProduct);

    try {
      // Update order_index for all affected products
      const updatePromises = newProducts.map((product, index) =>
        updateProduct({
          ...product,
          order: index
        })
      );

      await Promise.all(updatePromises);
    } catch (err) {
      console.error('Error updating product order:', err);
    }

    setDraggedIndex(null);
  };

  const handleShowDetails = (product: Product) => {
    setSelectedProduct(product);
    setShowProductDetails(true);
    // Lock scroll and hide navbar
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const handleEditClick = (product: Product) => {
    setEditingProduct(product);
    setShowEditModal(true);
    // Lock scroll and hide navbar
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const closeProductDetails = () => {
    setShowProductDetails(false);
    // Unlock scroll and show navbar
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingProduct(null);
    // Unlock scroll and show navbar
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.categories?.includes(selectedCategory));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <ParticleBackground />
      {/* Hero Section */}
      <section className="relative py-32 px-4 overflow-hidden z-10">
        <div className="relative max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <div className="flex items-center justify-center mb-6">
              <InfinityIcon className="w-16 h-16 text-green-500 mr-4" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-green-600 to-green-700 font-orbitron">
                CYBERTRONIC
              </h1>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-green-400 mb-6 font-orbitron">
              Unlimited
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              {t('unlimited.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {isAdmin && (
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowTextEditor(true)}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Upravit texty
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              onClick={() => {
                const adminParam = isAdmin ? '?admin=true' : '';
                navigate(`/vyzkum${adminParam}`);
                window.scrollTo(0, 0);
              }}
              className="bg-black/40 backdrop-blur-md border border-green-500/20 rounded-2xl p-8 text-center hover:border-green-400/40 transition-all hover:scale-105 cursor-pointer"
            >
              <div className="text-green-500 mb-6 flex justify-center">
                <Microscope className="w-16 h-16" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{editTexts.researchTitle || t('unlimited.researchTitle')}</h3>
              <p className="text-gray-300">
                {editTexts.researchDesc || t('unlimited.researchDesc')}
              </p>
              <p className="text-green-400 text-sm mt-4 font-medium">Zobrazit výzkum →</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              onClick={() => {
                const adminParam = isAdmin ? '?admin=true' : '';
                navigate(`/prototypy${adminParam}`);
                window.scrollTo(0, 0);
              }}
              className="bg-black/40 backdrop-blur-md border border-green-500/20 rounded-2xl p-8 text-center hover:border-green-400/40 transition-all hover:scale-105 cursor-pointer"
            >
              <div className="text-green-500 mb-6 flex justify-center">
                <Beaker className="w-16 h-16" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{editTexts.prototypesTitle || t('unlimited.prototypesTitle')}</h3>
              <p className="text-gray-300">
                {editTexts.prototypesDesc || t('unlimited.prototypesDesc')}
              </p>
              <p className="text-green-400 text-sm mt-4 font-medium">Zobrazit prototypy →</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              onClick={() => {
                const adminParam = isAdmin ? '?admin=true' : '';
                navigate(`/experimentalni${adminParam}`);
                window.scrollTo(0, 0);
              }}
              className="bg-black/40 backdrop-blur-md border border-green-500/20 rounded-2xl p-8 text-center hover:border-green-400/40 transition-all hover:scale-105 cursor-pointer"
            >
              <div className="text-green-500 mb-6 flex justify-center">
                <Zap className="w-16 h-16" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">{editTexts.experimentalTitle || t('unlimited.experimentalTitle')}</h3>
              <p className="text-gray-300">
                {editTexts.experimentalDesc || t('unlimited.experimentalDesc')}
              </p>
              <p className="text-green-400 text-sm mt-4 font-medium">Zobrazit experimentální →</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Admin Section */}
      <section className="py-20 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center mb-6">
              <ShoppingBag className="w-16 h-16 text-green-500 mr-4" />
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-green-600 to-green-700 font-orbitron">
                {t('navigation.products')}
              </h2>
            </div>
          </motion.div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-full font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {t('common.all')}
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
                style={{
                  backgroundColor: selectedCategory === category.id ? category.color : undefined
                }}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {filteredProducts.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                index={index}
                isAdmin={isAdmin}
                onEdit={handleEditClick}
                onDelete={handleDeleteProduct}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                language={currentLang}
                categories={categories}
                onShowDetails={handleShowDetails}
                sectionColor="green"
              />
            ))}
          </div>

          {/* Admin Add Product Button */}
          {isAdmin && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={async () => {
                  const newProduct: Product = {
                    id: '',
                    name: { cs: 'Nový produkt', en: 'New product', de: 'Neues Produkt', sk: 'Nový produkt' },
                    description: { cs: '', en: '', de: '', sk: '' },
                    price: 1000,
                    currency: 'CZK',
                    category: 'unlimited',
                    image: '/-ct_logo_light copy copy.png',
                    inStock: true,
                    featured: false,
                    discount: 0,
                    isOnSale: false,
                    isSoldOut: false,
                    isRecommended: false,
                    order: 0
                  };
                  try {
                    await addProduct(newProduct);
                  } catch (err) {
                    console.error('Error adding product:', err);
                  }
                }}
                className="bg-green-600 hover:bg-green-500 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>{t('common.addNewProduct')}</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCategoryManager(true)}
                className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
              >
                <span>{t('common.manageCategories')}</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowNewsManager(true)}
                className="bg-purple-600 hover:bg-purple-500 text-white px-8 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
              >
                <span>{t('common.manageNews')}</span>
              </motion.button>
            </div>
          )}
        </div>
      </section>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="animate-spin w-10 h-10 border-2 border-green-500 border-t-transparent rounded-full mb-4"></div>
              <p className="text-gray-400 text-sm">Načítání produktů…</p>
            </div>
          )}

      {/* Error State */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white p-4 rounded-lg shadow-lg z-50">
          <p className="text-sm">Chyba: {error}</p>
        </div>
      )}

      {/* Product Details Modal */}
      {showProductDetails && selectedProduct && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeProductDetails}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gray-900 border border-green-500/30 rounded-2xl p-6 w-[90vw] max-w-2xl max-h-[90vh] overflow-y-auto relative z-[60]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white">{selectedProduct.name[currentLang] || selectedProduct.name.cs}</h3>
              <button
                onClick={closeProductDetails}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="aspect-video bg-gray-700 rounded-lg mb-3 overflow-hidden">
              <ProductImageGallery product={selectedProduct} />
            </div>
            
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.description')}:</h4>
                <p className="text-gray-400 text-sm">
                  {selectedProduct.description[currentLang] || selectedProduct.description.cs || 'Popis není k dispozici'}
                </p>
              </div>
              
              {/* Manual download */}
              {selectedProduct.manual && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.manual')}:</h4>
                  <a
                    href={selectedProduct.manual}
                    download
                    className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                  >
                    <span>📄</span>
                    <span>{t('shop.downloadManual')}</span>
                  </a>
                </div>
              )}
              
              {/* Video tutorial */}
              {selectedProduct.videoTutorial && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.videoTutorial')}:</h4>
                  <a
                    href={selectedProduct.videoTutorial}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-500 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                  >
                    <span>🎥</span>
                    <span>{t('shop.playVideo')}</span>
                  </a>
                </div>
              )}
              
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.price')}:</h4>
                <div className="flex items-center space-x-3">
                  {selectedProduct.discount && selectedProduct.discount > 0 ? (
                    <>
                      <span className="text-gray-400 line-through text-sm">
                        {formatPrice(selectedProduct.price, i18n.language)}
                      </span>
                      <span className="text-green-500 font-bold text-base">
                        {formatPrice(selectedProduct.price * (1 - selectedProduct.discount / 100), i18n.language)}
                      </span>
                      <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                        -{selectedProduct.discount}%
                      </span>
                    </>
                  ) : (
                    <span className="text-green-500 font-bold text-base">
                      {formatPrice(selectedProduct.price, i18n.language)}
                    </span>
                  )}
                </div>
              </div>
              
              {selectedProduct.categories && selectedProduct.categories.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-300 mb-1">{t('shop.categories')}:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.categories.map((categoryId) => {
                      const category = categories.find(c => c.id === categoryId);
                      return category ? (
                        <span
                          key={categoryId} 
                          className="px-4 py-2 rounded-full text-sm font-bold text-white"
                          style={{backgroundColor: category.color}}
                        >
                          {category.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              <div className="flex space-x-4 pt-2">
                <button
                  onClick={async () => {
                    if (!selectedProduct.isSoldOut) {
                      // Close modal first
                      closeProductDetails();

                      // Add to cart with animation
                      const cart = JSON.parse(localStorage.getItem('cart') || '{}');
                      const currentQuantity = cart[selectedProduct.id] || 0;
                      cart[selectedProduct.id] = currentQuantity + 1;
                      localStorage.setItem('cart', JSON.stringify(cart));

                      // Store category
                      const cartCategories = JSON.parse(localStorage.getItem('cart_categories') || '{}');
                      cartCategories[selectedProduct.id] = selectedProduct.category;
                      localStorage.setItem('cart_categories', JSON.stringify(cartCategories));

                      // Dispatch cart update events
                      window.dispatchEvent(new Event('cartUpdated'));
                      window.dispatchEvent(new Event('globalCartUpdate'));

                      // Create floating animation
                      const centerX = window.innerWidth / 2;
                      const centerY = window.innerHeight / 2;

                      const floatingElement = document.createElement('div');
                      floatingElement.innerHTML = `
                        <div style="
                          width: 60px;
                          height: 60px;
                          background-image: url('${selectedProduct.image}');
                          background-size: cover;
                          background-position: center;
                          border-radius: 12px;
                          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
                          border: 3px solid #22c55e;
                        "></div>
                      `;

                      floatingElement.style.cssText = `
                        position: fixed;
                        left: ${centerX - 30}px;
                        top: ${centerY - 30}px;
                        z-index: 9999;
                        pointer-events: none;
                        transition: all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                        transform: scale(1);
                        opacity: 1;
                      `;

                      document.body.appendChild(floatingElement);

                      setTimeout(() => {
                        const cartIcon = document.querySelector('nav a[href="/cart"] svg');
                        if (cartIcon) {
                          const cartRect = cartIcon.getBoundingClientRect();
                          floatingElement.style.left = (cartRect.left + cartRect.width / 2 - 15) + 'px';
                          floatingElement.style.top = (cartRect.top + cartRect.height / 2 - 15) + 'px';
                          floatingElement.style.transform = 'scale(0.3)';
                          floatingElement.style.opacity = '0';
                        }
                      }, 100);

                      setTimeout(() => {
                        if (document.body.contains(floatingElement)) {
                          document.body.removeChild(floatingElement);
                        }
                      }, 1300);

                      // Create success notification
                      const currentLang = i18n.language === 'cz' ? 'cs' : i18n.language;
                      const productName = selectedProduct.name[currentLang] || selectedProduct.name.cs;

                      const notification = document.createElement('div');
                      notification.innerHTML = `
                        <div style="
                          background: linear-gradient(135deg, #22c55e, #16a34a);
                          color: white;
                          padding: 12px 20px;
                          border-radius: 12px;
                          box-shadow: 0 8px 32px rgba(34, 197, 94, 0.3);
                          font-weight: 600;
                          font-size: 14px;
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        ">
                          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          ${t('shop.addedToCart')}: 1x ${productName}
                        </div>
                      `;

                      notification.style.cssText = `
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        z-index: 9999;
                        pointer-events: none;
                        transform: translateX(100%);
                        transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                      `;

                      document.body.appendChild(notification);

                      setTimeout(() => {
                        notification.style.transform = 'translateX(0)';
                      }, 200);

                      setTimeout(() => {
                        notification.style.transform = 'translateX(100%)';
                      }, 2500);

                      setTimeout(() => {
                        if (document.body.contains(notification)) {
                          document.body.removeChild(notification);
                        }
                      }, 3000);
                    } else {
                      closeProductDetails();
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg hover:from-green-400 hover:to-green-500 transition-all font-semibold"
                  disabled={selectedProduct.isSoldOut}
                >
                  {selectedProduct.isSoldOut ? t('shop.soldOut') : t('shop.addToCart')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gray-900 border border-green-500/30 rounded-2xl p-8 w-[90vw] max-w-4xl max-h-[90vh] overflow-y-auto relative z-[60]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white">{t('common.editProduct')}</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <ProductEditForm
              product={editingProduct}
              categories={categories}
              themeColor="green-500"
              onSave={handleEditProduct}
              onCancel={closeEditModal}
            />
          </motion.div>
        </motion.div>
      )}

      {/* Category Manager */}
      {showCategoryManager && (
        <CategoryManager
          categories={categories}
          onSave={(updatedCategories) => {
            // Handle category updates through Supabase
            handleSaveCategories(updatedCategories);
          }}
          onClose={() => setShowCategoryManager(false)}
          themeColor="#16A34A"
          section="unlimited"
          addCategory={addCategory}
          editCategory={editCategory}
          removeCategory={removeCategory}
        />
      )}

      {/* News Manager */}
      {showNewsManager && (
        <NewsManager
          onClose={() => setShowNewsManager(false)}
          themeColor="#16A34A"
        />
      )}

      {/* Text Editor Modal */}
      {showTextEditor && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
          onClick={() => setShowTextEditor(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gradient-to-br from-gray-900 to-black border border-green-500/30 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-green-400">Upravit texty</h2>
              <button
                onClick={() => setShowTextEditor(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-white mb-2 font-semibold">Výzkum & Vývoj - Nadpis</label>
                <input
                  type="text"
                  value={editTexts.researchTitle}
                  onChange={(e) => setEditTexts({...editTexts, researchTitle: e.target.value})}
                  className="w-full bg-gray-800/50 border border-green-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-white mb-2 font-semibold">Výzkum & Vývoj - Popis</label>
                <textarea
                  value={editTexts.researchDesc}
                  onChange={(e) => setEditTexts({...editTexts, researchDesc: e.target.value})}
                  rows={3}
                  className="w-full bg-gray-800/50 border border-green-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-white mb-2 font-semibold">Prototypy - Nadpis</label>
                <input
                  type="text"
                  value={editTexts.prototypesTitle}
                  onChange={(e) => setEditTexts({...editTexts, prototypesTitle: e.target.value})}
                  className="w-full bg-gray-800/50 border border-green-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-white mb-2 font-semibold">Prototypy - Popis</label>
                <textarea
                  value={editTexts.prototypesDesc}
                  onChange={(e) => setEditTexts({...editTexts, prototypesDesc: e.target.value})}
                  rows={3}
                  className="w-full bg-gray-800/50 border border-green-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-white mb-2 font-semibold">Experimentální technologie - Nadpis</label>
                <input
                  type="text"
                  value={editTexts.experimentalTitle}
                  onChange={(e) => setEditTexts({...editTexts, experimentalTitle: e.target.value})}
                  className="w-full bg-gray-800/50 border border-green-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-white mb-2 font-semibold">Experimentální technologie - Popis</label>
                <textarea
                  value={editTexts.experimentalDesc}
                  onChange={(e) => setEditTexts({...editTexts, experimentalDesc: e.target.value})}
                  rows={3}
                  className="w-full bg-gray-800/50 border border-green-500/30 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-green-500"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => setShowTextEditor(false)}
                  className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors font-semibold"
                >
                  Zrušit
                </button>
                <button
                  onClick={handleSaveTexts}
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg hover:from-green-400 hover:to-green-500 transition-all font-semibold"
                >
                  Uložit změny
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Inquiry Form */}
      {showInquiryForm && (
        <InquiryForm
          onClose={() => setShowInquiryForm(false)}
          section="Cybertronic Unlimited"
        />
      )}

    </div>
  );
}


// Category Manager Component
interface CategoryManagerProps {
  categories: Category[];
  onSave: (categories: Category[]) => void;
  onClose: () => void;
  themeColor: string;
  section: string;
  addCategory: (data: { name: string; color: string }) => Promise<any>;
  editCategory: (id: string, data: { name: string; color: string }) => Promise<any>;
  removeCategory: (id: string) => Promise<void>;
}

function CategoryManager({ categories, onSave, onClose, themeColor, section, addCategory, editCategory, removeCategory }: CategoryManagerProps) {
  const { t } = useTranslation();
  const [editingCategories, setEditingCategories] = useState(categories);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddCategory = () => {
    const newCategory: Category = {
      id: `temp_${Date.now()}`,
      name: '',
      color: themeColor
    };
    setEditingCategories([...editingCategories, newCategory]);
  };

  const updateCategory = (id: string, field: string, value: string) => {
    setEditingCategories(prev =>
      prev.map(cat => cat.id === id ? { ...cat, [field]: value } : cat)
    );
  };

  const handleDeleteCategory = (id: string) => {
    setEditingCategories(prev => prev.filter(cat => cat.id !== id));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate that at least one category has a name
      const validCategories = editingCategories.filter(cat => cat.name.trim() !== '');

      if (validCategories.length === 0 && editingCategories.length > 0) {
        setError('Vyplňte prosím název alespoň jedné kategorie');
        setSaving(false);
        return;
      }

      // Process each category
      for (const category of editingCategories) {
        if (category.name.trim() === '') continue;

        try {
          if (category.id.startsWith('temp_')) {
            // New category
            console.log('Creating new category:', { name: category.name, color: category.color, section });
            const result = await addCategory({
              name: category.name,
              color: category.color
            });
            console.log('Category created successfully:', result);
          } else {
            // Existing category - only update if it changed
            const originalCategory = categories.find(cat => cat.id === category.id);
            if (originalCategory &&
                (originalCategory.name !== category.name ||
                 originalCategory.color !== category.color)) {
              console.log('Updating category:', { id: category.id, name: category.name, color: category.color });
              const result = await editCategory(category.id, {
                name: category.name,
                color: category.color
              });
              console.log('Category updated successfully:', result);
            }
          }
        } catch (categoryError) {
          console.error('Failed to save category:', category, categoryError);
          setError(`Chyba při ukládání kategorie "${category.name}": ${categoryError instanceof Error ? categoryError.message : 'Neznámá chyba'}`);
          setSaving(false);
          return;
        }
      }

      // Handle deleted categories
      const deletedCategories = categories.filter(
        cat => !editingCategories.find(editCat => editCat.id === cat.id)
      );

      for (const deletedCat of deletedCategories) {
        if (deletedCat.id && !deletedCat.id.startsWith('temp_')) {
          console.log('Deleting category:', deletedCat.id);
          await removeCategory(deletedCat.id);
        }
      }

      console.log('Categories saved successfully');

      // Show success notification
      const notification = document.createElement('div');
      notification.innerHTML = `
        <div style="
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(34, 197, 94, 0.3);
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        ">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Kategorie byly úspěšně uloženy
        </div>
      `;

      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 99999;
        pointer-events: none;
        transform: translateX(100%);
        transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.transform = 'translateX(0)';
      }, 100);

      setTimeout(() => {
        notification.style.transform = 'translateX(100%)';
      }, 2500);

      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      // Wait a bit for real-time subscription to update before closing
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (err) {
      console.error('Error saving categories:', err);
      setError(err instanceof Error ? err.message : 'Chyba při ukládání kategorií');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-green-500/30 rounded-2xl p-8 w-full max-w-4xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-2xl font-bold text-white">Správa kategorií</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-600/20 border border-red-500 rounded-lg p-4 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4 mb-6">
          {editingCategories.map((category) => (
            <div key={category.id} className="flex items-center space-x-4 bg-gray-800 p-4 rounded-lg">
              <input
                type="text"
                value={category.name}
                onChange={(e) => updateCategory(category.id, 'name', e.target.value)}
                placeholder="Název kategorie"
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                required
              />
              <input
                type="color"
                value={category.color}
                onChange={(e) => updateCategory(category.id, 'color', e.target.value)}
                className="w-12 h-10 rounded border border-gray-600"
              />
              <button
                onClick={() => handleDeleteCategory(category.id)}
                className="text-red-400 hover:text-red-300 p-2"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex space-x-4">
          <button
            onClick={handleAddCategory}
            className="bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            {t('common.addCategory')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors font-semibold"
          >
            Zrušit
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg hover:from-green-400 hover:to-green-500 transition-all font-semibold"
          >
            {saving ? 'Ukládání...' : 'Uložit změny'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// News Manager Component
interface NewsManagerProps {
  onClose: () => void;
  themeColor: string;
}

function NewsManager({ onClose, themeColor }: NewsManagerProps) {
  const { t } = useTranslation();
  const { news: supabaseNews, addNews: addSupabaseNews, updateNews: updateSupabaseNews, deleteNews: deleteSupabaseNews } = useSupabaseNews();
  const [editingNews, setEditingNews] = useState<any[]>([]);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditingNews(supabaseNews.map(n => ({ ...n })));
  }, [supabaseNews]);

  const handleAddNews = () => {
    const newArticle = {
      id: `temp_${Date.now()}`,
      title: { cs: '', en: '', de: '', sk: '' },
      excerpt: { cs: '', en: '', de: '', sk: '' },
      content: { cs: '', en: '', de: '', sk: '' },
      date: new Date().toISOString().split('T')[0],
      image: '/-ct_logo_light copy copy.png',
      category: 'product-launch'
    };
    setEditingNews([...editingNews, newArticle]);
  };

  const handleUpdateNews = (id: string, field: string, value: any, lang?: string) => {
    setEditingNews(prev =>
      prev.map(article => {
        if (article.id === id) {
          if (lang) {
            return { ...article, [field]: { ...article[field], [lang]: value } };
          } else {
            return { ...article, [field]: value };
          }
        }
        return article;
      })
    );
  };

  const handleDeleteNews = (id: string) => {
    if (!id.startsWith('temp_')) {
      setDeletedIds(prev => [...prev, id]);
    }
    setEditingNews(prev => prev.filter(article => article.id !== id));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const deletedId of deletedIds) {
        await deleteSupabaseNews(deletedId);
      }

      for (const article of editingNews) {
        if (article.title.cs.trim() === '' && article.title.en.trim() === '') continue;

        if (article.id.startsWith('temp_')) {
          await addSupabaseNews(article);
        } else {
          await updateSupabaseNews(article.id, article);
        }
      }

      const notification = document.createElement('div');
      notification.innerHTML = `
        <div style="
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
          padding: 12px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(34, 197, 94, 0.3);
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        ">
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          Novinky byly úspěšně uloženy
        </div>
      `;

      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 99999;
        pointer-events: none;
        transform: translateX(100%);
        transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.transform = 'translateX(0)';
      }, 100);

      setTimeout(() => {
        notification.style.transform = 'translateX(100%)';
      }, 2500);

      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      onClose();
    } catch (err) {
      console.error('Error saving news:', err);
      setError(err instanceof Error ? err.message : 'Chyba při ukládání novinek');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-green-500/30 rounded-2xl p-8 w-full max-w-6xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-2xl font-bold text-white">Správa novinek</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-600/20 border border-red-500 rounded-lg p-4 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-6 mb-6">
          {editingNews.map((article) => (
            <div key={article.id} className="bg-gray-800 p-6 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  value={article.title?.cs || ''}
                  onChange={(e) => handleUpdateNews(article.id, 'title', e.target.value, 'cs')}
                  placeholder="Název (CS)"
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                />
                <input
                  type="text"
                  value={article.title?.en || ''}
                  onChange={(e) => handleUpdateNews(article.id, 'title', e.target.value, 'en')}
                  placeholder="Název (EN)"
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  value={article.title?.de || ''}
                  onChange={(e) => handleUpdateNews(article.id, 'title', e.target.value, 'de')}
                  placeholder="Název (DE)"
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                />
                <input
                  type="text"
                  value={article.title?.sk || ''}
                  onChange={(e) => handleUpdateNews(article.id, 'title', e.target.value, 'sk')}
                  placeholder="Název (SK)"
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                />
              </div>
              <textarea
                value={article.excerpt?.cs || ''}
                onChange={(e) => handleUpdateNews(article.id, 'excerpt', e.target.value, 'cs')}
                placeholder="Krátký popis (CS)"
                rows={2}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none mb-4"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <textarea
                  value={article.excerpt?.en || ''}
                  onChange={(e) => handleUpdateNews(article.id, 'excerpt', e.target.value, 'en')}
                  placeholder="Krátký popis (EN)"
                  rows={2}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                />
                <textarea
                  value={article.excerpt?.de || ''}
                  onChange={(e) => handleUpdateNews(article.id, 'excerpt', e.target.value, 'de')}
                  placeholder="Krátký popis (DE)"
                  rows={2}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                />
              </div>
              <textarea
                value={article.excerpt?.sk || ''}
                onChange={(e) => handleUpdateNews(article.id, 'excerpt', e.target.value, 'sk')}
                placeholder="Krátký popis (SK)"
                rows={2}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none mb-4"
              />
              <div className="flex items-center space-x-4">
                <input
                  type="date"
                  value={article.date || ''}
                  onChange={(e) => handleUpdateNews(article.id, 'date', e.target.value)}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
                />
                <button
                  onClick={() => handleDeleteNews(article.id)}
                  className="text-red-400 hover:text-red-300 p-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex space-x-4">
          <button
            onClick={handleAddNews}
            className="bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            {t('common.addNews')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600 transition-colors font-semibold"
          >
            Zrušit
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-3 rounded-lg hover:from-green-400 hover:to-green-500 transition-all font-semibold"
          >
            {saving ? 'Ukládání...' : 'Uložit změny'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

